'use client';

import React, { useMemo, useState } from 'react';
import { usePlanner } from '../plannerContext';
import { buildDependencyGraph, programColor, requirementsForCourse, shortCode, type GraphNode } from '../plannerModel';

const COL_W = 212;
const NODE_W = 180;
const NODE_H = 50;
const ROW_H = 64;
const HEAD_H = 40;

export default function GraphView() {
  const { terms, index, completed, placement, programs, issuesByCode, nameOf, open, selectedCode } = usePlanner();
  const [hovered, setHovered] = useState<string | null>(null);

  const graph = useMemo(
    () => buildDependencyGraph(terms, index, completed, placement, programs, new Set(issuesByCode.keys()), nameOf),
    [terms, index, completed, placement, programs, issuesByCode, nameOf],
  );

  const nodeByCode = useMemo(() => new Map(graph.nodes.map((n) => [n.code, n])), [graph.nodes]);
  const usedColumns = useMemo(() => {
    const used = new Set(graph.nodes.map((n) => n.column));
    // Keep every term column so the timeline reads continuously; drop empty Earned/next columns.
    return graph.columns.map((_, i) => i).filter((i) => (i > 0 && i < graph.columns.length - 1) || used.has(i));
  }, [graph]);
  const columnX = (column: number) => usedColumns.indexOf(column) * COL_W;
  const rows = Math.max(1, ...graph.nodes.map((n) => n.row + 1));
  const width = usedColumns.length * COL_W - (COL_W - NODE_W);
  const height = HEAD_H + rows * ROW_H;

  const focus = hovered || selectedCode;
  const lit = useMemo(() => {
    if (!focus || !nodeByCode.has(focus)) return null;
    const set = new Set([focus]);
    const walk = (code: string, dir: 'up' | 'down') => {
      graph.edges.forEach((edge) => {
        const next = dir === 'up' ? (edge.to === code ? edge.from : null) : (edge.from === code ? edge.to : null);
        if (next && !set.has(next)) { set.add(next); walk(next, dir); }
      });
    };
    walk(focus, 'up');
    walk(focus, 'down');
    return set;
  }, [focus, graph.edges, nodeByCode]);

  const pos = (node: GraphNode) => ({ x: columnX(node.column), y: HEAD_H + node.row * ROW_H });

  if (!graph.nodes.length) {
    return (
      <div className="rounded-2xl border border-dashed border-[var(--ss-border)] p-8 text-center text-sm text-[var(--ss-text-muted)]">
        Plan a few courses to see how they unlock each other.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-[var(--ss-text-muted)]">
        <p className="max-w-2xl">Lines run from a prerequisite to the course it opens. Hover or select a course to trace its chain. The last column suggests requirement courses this plan makes reachable.</p>
        <div className="flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-[var(--ss-positive)]" aria-hidden="true" />Earned</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded border border-[var(--ss-border)] bg-[var(--ss-surface)]" aria-hidden="true" />Planned</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded border border-dashed border-[var(--ss-text-muted)]" aria-hidden="true" />Unlocks next</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-0 w-4 border-t-2 border-dashed border-[var(--ss-critical)]" aria-hidden="true" />Order problem</span>
        </div>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-[var(--ss-border)] bg-[var(--ss-surface)] p-4">
        <div className="relative" style={{ width, height }}>
          {usedColumns.map((column) => (
            <div key={column} className="absolute top-0 flex h-8 items-center gap-2 text-xs font-bold" style={{ left: columnX(column), width: NODE_W }}>
              <span className="truncate">{graph.columns[column]}</span>
              {column > 0 && column < graph.columns.length - 1 ? <span className="font-normal tabular-nums text-[var(--ss-text-muted)]">{terms[column - 1]?.credits} cr</span> : null}
            </div>
          ))}
          <svg className="pointer-events-none absolute inset-0" width={width} height={height} aria-hidden="true">
            {usedColumns.slice(1).map((column) => (
              <line key={column} x1={columnX(column) - (COL_W - NODE_W) / 2} x2={columnX(column) - (COL_W - NODE_W) / 2} y1={4} y2={height} stroke="var(--ss-border)" strokeDasharray="2 4" />
            ))}
            {graph.edges.map((edge, i) => {
              const from = nodeByCode.get(edge.from);
              const to = nodeByCode.get(edge.to);
              if (!from || !to) return null;
              const a = pos(from);
              const b = pos(to);
              const x1 = a.x + NODE_W;
              const y1 = a.y + NODE_H / 2;
              const x2 = b.x;
              const y2 = b.y + NODE_H / 2;
              const sameColumn = from.column === to.column;
              const d = sameColumn
                ? `M ${x1} ${y1} C ${x1 + 26} ${y1}, ${x1 + 26} ${y2}, ${x1} ${y2}`
                : `M ${x1} ${y1} C ${x1 + (x2 - x1) / 2} ${y1}, ${x1 + (x2 - x1) / 2} ${y2}, ${x2} ${y2}`;
              const active = lit ? lit.has(edge.from) && lit.has(edge.to) : false;
              const stroke = edge.late ? 'var(--ss-critical)' : active ? 'var(--ss-brand)' : 'var(--ss-text-muted)';
              return (
                <path key={i} d={d} fill="none" stroke={stroke} strokeWidth={active ? 2 : 1.25}
                  strokeOpacity={lit && !active ? 0.15 : active ? 1 : 0.45} strokeDasharray={edge.late || to.kind === 'next' ? '5 4' : undefined} />
              );
            })}
          </svg>
          {graph.nodes.map((node) => {
            const p = pos(node);
            const counts = requirementsForCourse(node.code, programs);
            const accent = counts.length ? programColor(counts[0].programIndex) : 'var(--ss-border)';
            const dim = lit ? !lit.has(node.code) : false;
            const base = node.kind === 'earned'
              ? 'bg-[var(--ss-positive-bg)] border-[color-mix(in_srgb,var(--ss-positive)_35%,var(--ss-border))]'
              : node.kind === 'next'
                ? 'bg-transparent border-dashed border-[var(--ss-text-muted)]/60'
                : 'bg-[var(--ss-surface)] border-[var(--ss-border)]';
            return (
              <button
                key={node.code}
                type="button"
                data-plan-code={node.code}
                onMouseEnter={() => setHovered(node.code)}
                onMouseLeave={() => setHovered(null)}
                onFocus={() => setHovered(node.code)}
                onBlur={() => setHovered(null)}
                onClick={(e) => open({ kind: 'course', code: node.code, anchor: e.currentTarget })}
                aria-label={`${node.title}, ${node.kind === 'next' ? 'not planned yet' : node.kind}${node.hasIssue ? ', has a plan issue' : ''}`}
                className={`absolute flex flex-col justify-center overflow-hidden rounded-[10px] border py-1 pl-3 pr-2 text-left transition-opacity duration-150 motion-reduce:transition-none ${base} ${dim ? 'opacity-25' : ''} ${node.hasIssue ? 'ring-2 ring-[var(--ss-critical)]' : ''} ${selectedCode === node.code ? 'ring-2 ring-[var(--ss-brand)]' : ''}`}
                style={{ left: p.x, top: p.y, width: NODE_W, height: NODE_H }}
              >
                <span className="absolute bottom-1.5 left-0 top-1.5 w-[3px] rounded-r" style={{ backgroundColor: node.kind === 'earned' ? 'var(--ss-positive)' : accent }} aria-hidden="true" />
                <span className="truncate text-[12px] font-semibold leading-tight">{node.title}</span>
                <span className="font-mono text-[10.5px] text-[var(--ss-text-muted)]">{shortCode(node.code)}{node.kind === 'next' ? ' · not planned' : ''}</span>
              </button>
            );
          })}
        </div>
      </div>
      <p className="text-[11px] text-[var(--ss-text-muted)]">Built from catalog prerequisite text. Earned courses appear only when they feed something in the plan.</p>
    </div>
  );
}
